import { useRef } from "react";
import { toast } from "sonner";
import { ImagePlus, Tag, CalendarClock } from "lucide-react";
import { shrinkImage } from "../lib/helpers";
import { Field, PlainSelect } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";

export function PhotoField({ preview, onPick, onRemove }) {
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { onPick(await shrinkImage(f)); } catch { toast.error("Couldn't read this image"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="camp-photo">
      {preview && <img src={preview} alt="" className="h-20 w-32 rounded-md object-cover border" data-testid="camp-photo-img" />}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="camp-photo-input" onChange={pick} />
      <Button type="button" size="sm" variant="outline" className="rounded-full" data-testid="camp-photo-add" onClick={() => ref.current.click()}><ImagePlus className="h-4 w-4 mr-1" />{preview ? "Change photo" : "Add email photo"}</Button>
      {preview && <Button type="button" size="sm" variant="ghost" data-testid="camp-photo-remove" onClick={onRemove}>Remove photo</Button>}
    </div>
  );
}

const OFFER0 = { code: "", type: "percent", value: 15, expires_on: "", recipients_only: true };

export function OfferFields({ offer, onChange }) {
  const set = (k) => (e) => onChange({ ...offer, [k]: e.target.value });
  return (
    <div className="rounded-lg border p-4 space-y-3 bg-muted/30" data-testid="camp-offer">
      <label className="flex items-center gap-2 text-sm font-semibold"><Switch data-testid="camp-offer-toggle" checked={!!offer} onCheckedChange={(v) => onChange(v ? OFFER0 : null)} /><Tag className="h-4 w-4 text-primary" />Include a discount code</label>
      {offer && <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Field label="Code (blank = automatic)"><Input data-testid="camp-offer-code" className="uppercase" placeholder="SPRING20" value={offer.code} onChange={set("code")} /></Field>
          <Field label="Type"><PlainSelect data-testid="camp-offer-type" value={offer.type} onChange={set("type")}><option value="percent">% off</option><option value="amount">Fixed amount off</option></PlainSelect></Field>
          <Field label={offer.type === "percent" ? "Percent" : "Amount"}><Input type="number" min={1} data-testid="camp-offer-value" value={offer.value} onChange={set("value")} /></Field>
          <Field label="Expires (optional)"><Input type="date" data-testid="camp-offer-expires" value={offer.expires_on} onChange={set("expires_on")} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="camp-offer-recipients" checked={offer.recipients_only} onCheckedChange={(v) => onChange({ ...offer, recipients_only: v })} />
          {offer.recipients_only ? "Only people who received this campaign can use it" : "Anyone with the code can use it"}</label>
        <p className="text-xs text-muted-foreground">One use per person · works for online booking, the shop and in the clinic · the code and a “Book with my code” button are added under your message. Use {"{code}"} to mention it in the text.</p>
      </>}
    </div>
  );
}

export function ScheduleField({ value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="camp-schedule">
      <label className="flex items-center gap-2 text-sm"><Switch data-testid="camp-schedule-toggle" checked={value !== null} onCheckedChange={(v) => onChange(v ? "" : null)} /><CalendarClock className="h-4 w-4 text-primary" />Schedule for later</label>
      {value !== null && <Input type="datetime-local" className="w-auto" data-testid="camp-schedule-at" value={value} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}
